import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { customerIdSelector } from 'packages/store/user/user.selectors';
import { subscribeDesignsThumbnailsOptions, useSocketSubscribe, type ThumbnailWSData } from 'packages/socket';
import { useSseSubscribe, useIsSseEnabled, sseDesignsThumbnailsOptions } from 'packages/sse';
import { updateThumbnails } from 'packages/store/creatives/creatives.actions';
import { updateCollectionsThumbnails } from 'packages/store/collections/collections.actions';
import { updateTemplateThumbnails } from 'packages/store/templates/templates.actions';
import { updateCategoryThumbnails } from 'packages/store/categories/categories.actions';
import useSuperSet from './useSuperSet';

const emptyCreativeIds: number[] = [];

/** Listen for design thumbnail updates via ws or sse, depending on the flag */
export default function useCreativeThumbnailsUpdates(pendingIds: number[]) {
    const customerId = useSelector(customerIdSelector);
    const isSseEnabled = useIsSseEnabled();

    const dispatch = useDispatch();

    // prevents unnecessary re-subscriptions when pendingIds change
    // the array stays the same until there is no new items in pendingIds
    const memoizedPendingIds = useSuperSet(pendingIds);
    const creativeIds = pendingIds.length ? memoizedPendingIds : emptyCreativeIds;

    // Not memoised on purpose: both hooks hold it in a ref.
    const onMessage = ({ items }: { items: ThumbnailWSData[] }) => {
        dispatch(updateThumbnails(items));
        dispatch(updateTemplateThumbnails(items));
        dispatch(updateCollectionsThumbnails(items));
        dispatch(updateCategoryThumbnails(items));
    };

    // Both are called every render; only the selected one is ever invoked. Neither opens a
    // connection at call time, so the unused one costs nothing.
    const subscribeViaSocket = useSocketSubscribe(
        subscribeDesignsThumbnailsOptions({ customerId, creativeIds, onMessage }),
    );
    const subscribeViaSse = useSseSubscribe(sseDesignsThumbnailsOptions({ creativeIds, onMessage }));
    const subscribeDesignThumbnails = isSseEnabled ? subscribeViaSse : subscribeViaSocket;

    useEffect(() => {
        if (!creativeIds.length) {
            return;
        }

        return subscribeDesignThumbnails();
    }, [creativeIds, subscribeDesignThumbnails]);
}
